<template>
  <section class="timeline-section">
    <div class="timeline-wrapper" id="timeline-wrapper" ref="wrapper" @wheel.prevent="handleWheel">
      <div class="timeline-detail" id="timeline-detail" ref="detail" @mousedown="handleMouseDown">
        <canvas id="timeline-canvas" ref="canvas"></canvas>
        <div id="playhead" ref="playhead"></div>
        <div id="selection-overlay" :class="['selection-overlay', { hidden: !hasSelection }]" :style="selectionStyle">
          <div class="selection-handle selection-handle-left" id="selection-handle-left" @mousedown.stop="startDrag('left', $event)"></div>
          <div class="selection-handle selection-handle-right" id="selection-handle-right" @mousedown.stop="startDrag('right', $event)"></div>
        </div>
      </div>
    </div>
    <div class="time-axis" id="time-axis" ref="timeAxis"></div>
  </section>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { getTypeCategory, truncateText } from '../services/helpers'

export default {
  name: 'AudioTimeline',
  data() { return { 
    ctx: null, 
    dragStartX: 0, 
    dragStartTime: 0,
    isPanning: false,
    panStartX: 0,
    panStartViewStart: 0,
    panStartViewEnd: 0,
    hasDragged: false,
    wheelThrottled: false,
    lastVisibleSegmentIds: null,
    selectionStartX: 0,
    selectionIsClick: true,
    selectionShift: false,
    savedSelectionStart: null,
    savedSelectionEnd: null
  } },
  computed: {
    ...mapState(['viewStart', 'viewEnd', 'viewDuration', 'currentTime', 'segments', 'metadata', 'selectionMode', 'selectionStart', 'selectionEnd', 'isSelecting', 'draggingHandle', 'isLive', 'isPlaying', 'exportCuts']),
    ...mapGetters(['typeCategories', 'defaultTypeColor']),
    hasSelection() { return this.selectionStart !== null && this.selectionEnd !== null },
    // Compute visible segment IDs for change detection
    visibleSegmentIds() {
      const start = this.viewStart
      const end = this.viewEnd
      return this.segments
        .filter(s => {
          const segEnd = s.timestamp + (s.actualDuration || s.duration || 60000)
          return segEnd >= start && s.timestamp <= end
        })
        .map(s => s.id)
        .join(',')
    },
    selectionStyle() {
      if (!this.hasSelection) return {}
      const range = this.viewEnd - this.viewStart
      const wrapper = this.$refs.wrapper
      const w = wrapper ? wrapper.getBoundingClientRect().width : 1000
      const start = Math.min(this.selectionStart, this.selectionEnd)
      const end = Math.max(this.selectionStart, this.selectionEnd)
      const left = ((start - this.viewStart) / range) * w
      const right = ((end - this.viewStart) / range) * w
      return { left: Math.max(0, left) + 'px', width: (Math.min(w, right) - Math.max(0, left)) + 'px' }
    }
  },
  watch: {
    viewStart() { this.render() },
    viewEnd() { this.render() },
    // Re-render when segments change
    segments: {
      handler() { this.render() },
      deep: false
    },
    // Only re-render if visible segments actually changed
    visibleSegmentIds(newVal) {
      if (newVal !== this.lastVisibleSegmentIds) {
        this.lastVisibleSegmentIds = newVal
        this.render()
      }
    },
    metadata() { this.render() },
    currentTime() { this.updatePlayhead() },
    exportCuts: {
      handler() { this.render() },
      deep: true
    }
  },
  mounted() {
    this.setupCanvas()
    window.addEventListener('resize', this.handleResize)
    document.addEventListener('mousemove', this.handleMouseMove)
    document.addEventListener('mouseup', this.handleMouseUp)

    // ResizeObserver is more reliable than window.resize for catching
    // layout changes when the wrapper goes from 0 → real width
    if (window.ResizeObserver) {
      this._resizeObserver = new ResizeObserver(() => { this.handleResize() })
      if (this.$refs.wrapper) this._resizeObserver.observe(this.$refs.wrapper)
    }

    // Wait for the DOM to fully lay out before the first render
    this.$nextTick(() => { this.render() })
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
    document.removeEventListener('mousemove', this.handleMouseMove)
    document.removeEventListener('mouseup', this.handleMouseUp)
    if (this._resizeObserver) this._resizeObserver.disconnect()
  },
  methods: {
    setupCanvas() {
      this.ctx = this.$refs.canvas.getContext('2d')
      this.handleResize()
    },
    handleResize() {
      this.render()
    },
    render() {
      const canvas = this.$refs.canvas
      const wrapper = this.$refs.wrapper
      if (!canvas || !wrapper) return

      const ctx = this.ctx
      if (!ctx) return

      const rect = wrapper.getBoundingClientRect()
      const dpr = window.devicePixelRatio || 1
      const w = rect.width
      const h = rect.height

      // If the wrapper hasn't been laid out yet, retry after next paint
      if (w === 0 || h === 0) {
        this.$nextTick(() => { this.render() })
        return
      }
      
      // Set canvas size with DPR for crisp text
      canvas.width = w * dpr
      canvas.height = h * dpr
      canvas.style.width = w + 'px'
      canvas.style.height = h + 'px'
      ctx.scale(dpr, dpr)
      
      // Background - white (no recording)
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, w, h)
      
      const range = this.viewEnd - this.viewStart
      if (range <= 0) return
      
      const now = Date.now()
      const sortedSegments = [...this.segments].sort((a, b) => a.timestamp - b.timestamp)

      // Helper: get accurate end time for a segment without hardcoded 60s fallback.
      // - If actualDuration is set: use it (completed segment)
      // - If it's the last segment (currently recording): grow to now in real time
      // - If there's a next segment: extend to its timestamp (fills gaps exactly)
      // - Otherwise fall back to 60s (should rarely happen)
      const segmentEnd = (seg, idx) => {
        if (seg.actualDuration) return seg.timestamp + seg.actualDuration
        const next = sortedSegments[idx + 1]
        if (next) return next.timestamp
        return Math.max(seg.timestamp + (seg.duration || 60000), now)
      }
      const allSorted = [...this.metadata].sort((a, b) => a.timestamp - b.timestamp)
      
      // Find visible tracks (including the most recent one before viewStart)
      const visible = []
      let lastBeforeView = null
      for (let i = 0; i < allSorted.length; i++) {
        const m = allSorted[i]
        const next = allSorted[i + 1]
        const endTime = next ? next.timestamp : Math.max(now, m.timestamp + 60000)
        
        // Track the most recent metadata before the view
        if (m.timestamp < this.viewStart) {
          lastBeforeView = m
        }
        
        if (endTime >= this.viewStart && m.timestamp <= this.viewEnd) visible.push(m)
      }
      
      // If no metadata starts in view, but there's one before, include it
      // (its color extends into our view until the next metadata entry)
      if (lastBeforeView && !visible.includes(lastBeforeView)) {
        visible.unshift(lastBeforeView)
      }
      
      // Draw colored track blocks
      for (let i = 0; i < visible.length; i++) {
        const m = visible[i]
        const typeCategory = getTypeCategory(m.type, this.typeCategories, this.defaultTypeColor)
        const fullIdx = allSorted.findIndex(meta => meta.id === m.id)
        const next = allSorted[fullIdx + 1]
        let endTime = next ? next.timestamp : Math.max(now, m.timestamp + 60000)
        
        // Find segments that overlap with this track
        const trackSegments = sortedSegments.filter((seg, idx) => {
          const segEnd = segmentEnd(seg, idx)
          return segEnd > m.timestamp && seg.timestamp < endTime
        })
        
        // Draw colored block for each segment portion
        for (const seg of trackSegments) {
          const segEnd = segmentEnd(seg, sortedSegments.indexOf(seg))
          const blockStart = Math.max(m.timestamp, seg.timestamp)
          const blockEnd = Math.min(endTime, segEnd)
          if (blockEnd > blockStart) {
            const x = ((blockStart - this.viewStart) / range) * w
            const bw = Math.max(2, ((blockEnd - blockStart) / range) * w) + 1
            ctx.fillStyle = typeCategory.color
            ctx.fillRect(x, 0, bw, h)
          }
        }
        
        // Draw separator line at track start
        const trackX = ((m.timestamp - this.viewStart) / range) * w
        if (trackX >= 0 && trackX <= w && trackSegments.length > 0) {
          ctx.fillStyle = 'rgba(0, 0, 0, 0.15)'
          ctx.fillRect(Math.round(trackX), 0, 1, h)
        }
        
        // Labels - only if track has enough visible width
        const trackStartX = Math.max(0, trackX)
        const trackEndX = Math.min(w, ((endTime - this.viewStart) / range) * w)
        const visibleWidth = trackEndX - trackStartX
        
        if (visibleWidth > 100) {
          ctx.fillStyle = '#ffffff'
          ctx.font = 'bold 11px Poppins, -apple-system, sans-serif'
          ctx.textBaseline = 'top'
          const titleArtist = m.trackTitle 
            ? (m.artist ? `${m.artist} - ${m.trackTitle}` : m.trackTitle)
            : (m.artist ? `${m.title} - ${m.artist}` : m.title)
          const titleText = truncateText(ctx, titleArtist, visibleWidth - 16)
          ctx.fillText(titleText, trackStartX + 8, 8)
          ctx.font = '10px Poppins, -apple-system, sans-serif'
          ctx.fillStyle = 'rgba(255, 255, 255, 0.8)'
          ctx.fillText(typeCategory.label, trackStartX + 8, 24)
        } else if (visibleWidth > 40) {
          ctx.fillStyle = '#ffffff'
          ctx.font = '9px Poppins, -apple-system, sans-serif'
          ctx.textBaseline = 'top'
          const shortLabel = truncateText(ctx, typeCategory.label, visibleWidth - 8)
          ctx.fillText(shortLabel, trackStartX + 4, 8)
        }
      }
      
      // Draw waveforms on top of colored blocks
      this.renderWaveforms(ctx, sortedSegments, w, h, range, segmentEnd)
      
      // Draw queued export cuts indicators
      if (this.exportCuts && this.exportCuts.length > 0) {
        for (let i = 0; i < this.exportCuts.length; i++) {
          const cut = this.exportCuts[i]
          const cutStart = cut.start
          const cutEnd = cut.end
          
          // Check if cut is visible in current view
          if (cutEnd >= this.viewStart && cutStart <= this.viewEnd) {
            const x1 = Math.max(0, ((cutStart - this.viewStart) / range) * w)
            const x2 = Math.min(w, ((cutEnd - this.viewStart) / range) * w)
            const cutWidth = x2 - x1
            
            // Draw semi-transparent overlay
            ctx.fillStyle = 'rgba(16, 185, 129, 0.15)' // Green tint
            ctx.fillRect(x1, 0, cutWidth, h)
            
            // Draw border lines
            ctx.strokeStyle = 'rgba(16, 185, 129, 0.8)'
            ctx.lineWidth = 2
            ctx.setLineDash([4, 4])
            
            // Left border
            if (cutStart >= this.viewStart && cutStart <= this.viewEnd) {
              ctx.beginPath()
              ctx.moveTo(x1, 0)
              ctx.lineTo(x1, h)
              ctx.stroke()
            }
            
            // Right border
            if (cutEnd >= this.viewStart && cutEnd <= this.viewEnd) {
              ctx.beginPath()
              ctx.moveTo(x2, 0)
              ctx.lineTo(x2, h)
              ctx.stroke()
            }
            
            ctx.setLineDash([])
            
            // Draw cut number badge
            if (cutWidth > 30) {
              const badgeX = x1 + 4
              const badgeY = h - 22
              const badgeSize = 18
              
              // Badge background
              ctx.fillStyle = 'rgba(16, 185, 129, 0.9)'
              ctx.beginPath()
              ctx.arc(badgeX + badgeSize/2, badgeY + badgeSize/2, badgeSize/2, 0, Math.PI * 2)
              ctx.fill()
              
              // Badge number
              ctx.fillStyle = '#ffffff'
              ctx.font = 'bold 10px Poppins, -apple-system, sans-serif'
              ctx.textBaseline = 'middle'
              ctx.textAlign = 'center'
              ctx.fillText((i + 1).toString(), badgeX + badgeSize/2, badgeY + badgeSize/2)
              ctx.textAlign = 'left'
            }
          }
        }
      }
      
      // Draw "now" marker
      if (now >= this.viewStart && now <= this.viewEnd) {
        const nx = ((now - this.viewStart) / range) * w
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.4)'
        ctx.lineWidth = 2
        ctx.beginPath()
        ctx.moveTo(nx, 0)
        ctx.lineTo(nx, h)
        ctx.stroke()
      }
      
      this.renderTimeAxis()
      this.updatePlayhead()
    },
    renderWaveforms(ctx, sortedSegments, w, h, range, segmentEnd) {
      // Draw waveform peaks for segments that have them
      const visibleSegments = sortedSegments.filter((seg, idx) => {
        const segEnd = segmentEnd(seg, idx)
        return segEnd >= this.viewStart && seg.timestamp <= this.viewEnd && seg.peaks && seg.peaks.length > 0
      })
      
      if (visibleSegments.length === 0) return
      
      // Waveform style - semi-transparent white overlay
      ctx.fillStyle = 'rgba(255, 255, 255, 0.35)'
      
      // Position waveform in lower portion of track to avoid covering text labels
      // Text is at top (~8-40px), so we render waveform from middle-bottom
      const textAreaHeight = 40 // Reserve space for text labels at top
      const waveformAreaHeight = h - textAreaHeight
      const centerY = textAreaHeight + (waveformAreaHeight / 2)
      const maxAmplitude = waveformAreaHeight * 0.45 // Max height within waveform area
      
      // Sort segments by timestamp for gap filling
      const sortedVisible = [...visibleSegments].sort((a, b) => a.timestamp - b.timestamp)
      
      for (let segIdx = 0; segIdx < sortedVisible.length; segIdx++) {
        const seg = sortedVisible[segIdx]
        const nextSeg = sortedVisible[segIdx + 1]
        
        const segStart = seg.timestamp
        // Extend to next segment start to fill gaps, or use actual end
        const naturalEnd = segmentEnd(seg, sortedSegments.indexOf(seg))
        const segEnd = nextSeg ? Math.max(naturalEnd, nextSeg.timestamp) : naturalEnd
        const peaks = seg.peaks
        
        // Calculate visible portion of this segment
        const visibleStart = Math.max(segStart, this.viewStart)
        const visibleEnd = Math.min(segEnd, this.viewEnd)
        
        // X positions
        const x1 = ((visibleStart - this.viewStart) / range) * w
        const x2 = ((visibleEnd - this.viewStart) / range) * w
        const segWidth = x2 - x1
        
        if (segWidth < 2) continue // Skip if too narrow
        
        // Calculate which peaks to draw based on natural duration
        const naturalDuration = seg.actualDuration || seg.duration || Math.max(1, segmentEnd(seg, sortedSegments.indexOf(seg)) - seg.timestamp)
        const peakStartRatio = Math.max(0, (visibleStart - segStart) / naturalDuration)
        const peakEndRatio = Math.min(1, (visibleEnd - segStart) / naturalDuration)
        const startPeakIdx = Math.floor(peakStartRatio * peaks.length)
        const endPeakIdx = Math.min(Math.ceil(peakEndRatio * peaks.length), peaks.length - 1)
        
        // Draw waveform - distribute peaks across visible width
        ctx.beginPath()
        
        const numPeaksToDraw = endPeakIdx - startPeakIdx
        if (numPeaksToDraw <= 0) continue
        
        let firstPoint = true
        for (let i = startPeakIdx; i <= endPeakIdx; i++) {
          const localIdx = i - startPeakIdx
          const x = x1 + (localIdx / numPeaksToDraw) * segWidth
          const amplitude = peaks[i] * maxAmplitude
          
          if (firstPoint) {
            ctx.moveTo(x, centerY - amplitude)
            firstPoint = false
          } else {
            ctx.lineTo(x, centerY - amplitude)
          }
        }
        
        // Draw bottom half (mirror)
        for (let i = endPeakIdx; i >= startPeakIdx; i--) {
          const localIdx = i - startPeakIdx
          const x = x1 + (localIdx / numPeaksToDraw) * segWidth
          const amplitude = peaks[i] * maxAmplitude
          ctx.lineTo(x, centerY + amplitude)
        }
        
        ctx.closePath()
        ctx.fill()
      }
    },
    renderTimeAxis() {
      const container = this.$refs.timeAxis
      if (!container) return
      container.innerHTML = ''
      const range = this.viewEnd - this.viewStart
      if (range <= 0) return
      
      let tickInterval
      if (range <= 10 * 60 * 1000) tickInterval = 60 * 1000
      else if (range <= 30 * 60 * 1000) tickInterval = 5 * 60 * 1000
      else if (range <= 2 * 60 * 60 * 1000) tickInterval = 15 * 60 * 1000
      else tickInterval = 30 * 60 * 1000
      
      const firstTick = Math.ceil(this.viewStart / tickInterval) * tickInterval
      for (let t = firstTick; t <= this.viewEnd; t += tickInterval) {
        const pos = ((t - this.viewStart) / range) * 100
        if (pos < 0 || pos > 100) continue
        const d = new Date(t)
        const label = d.getHours().toString().padStart(2, '0') + ':' + d.getMinutes().toString().padStart(2, '0')
        const tick = document.createElement('div')
        tick.className = 'tick'
        tick.style.left = pos + '%'
        tick.textContent = label
        container.appendChild(tick)
      }
    },
    updatePlayhead() {
      const ph = this.$refs.playhead
      if (!ph) return
      const wrapper = this.$refs.wrapper
      if (!wrapper) return
      const w = wrapper.getBoundingClientRect().width
      
      // Playhead is ALWAYS at center - the timeline moves, not the playhead
      ph.style.display = 'block'
      ph.style.left = (w * 0.5) + 'px'
      ph.style.opacity = '1'
    },
    handleClick() {
      // Click is handled in mouseUp if we didn't drag
    },
    handleMouseDown(e) {
      // Selection mode - start selection
      if (this.selectionMode) {
        if (this.isLive || this.isPlaying) return
        const rect = this.$refs.canvas.getBoundingClientRect()
        const x = e.clientX - rect.left
        const t = this.viewStart + (x / rect.width) * (this.viewEnd - this.viewStart)
        this.selectionStartX = x
        this.selectionIsClick = true
        this.selectionShift = e.shiftKey
        
        // If shift is held and we have an existing selection, preserve it
        // (we'll extend it on mouseUp if it was a click)
        if (e.shiftKey && this.selectionStart !== null && this.selectionEnd !== null) {
          this.savedSelectionStart = Math.min(this.selectionStart, this.selectionEnd)
          this.savedSelectionEnd = Math.max(this.selectionStart, this.selectionEnd)
        } else {
          this.savedSelectionStart = null
          this.savedSelectionEnd = null
        }
        
        this.$store.dispatch('startSelection', t)
        e.preventDefault()
        return
      }
      
      // Normal mode - start potential pan
      this.isPanning = true
      this.hasDragged = false
      this.panStartX = e.clientX
      this.panStartViewStart = this.viewStart
      this.panStartViewEnd = this.viewEnd
      e.preventDefault()
    },
    handleMouseMove(e) {
      // Handle selection dragging
      if (this.isSelecting) {
        const rect = this.$refs.canvas.getBoundingClientRect()
        const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left))
        const t = this.viewStart + (x / rect.width) * (this.viewEnd - this.viewStart)
        // Track if user actually dragged (more than 5px)
        if (Math.abs(x - this.selectionStartX) > 5) {
          this.selectionIsClick = false
        }
        this.$store.dispatch('updateSelection', t)
        return
      }
      
      // Handle selection handle dragging
      if (this.draggingHandle) {
        const rect = this.$refs.canvas.getBoundingClientRect()
        const range = this.viewEnd - this.viewStart
        const deltaX = e.clientX - this.dragStartX
        const deltaTime = (deltaX / rect.width) * range
        let newTime = this.dragStartTime + deltaTime
        const now = Date.now()
        newTime = Math.max(this.viewStart, Math.min(now, newTime))
        const start = Math.min(this.selectionStart, this.selectionEnd)
        const end = Math.max(this.selectionStart, this.selectionEnd)
        if (this.draggingHandle === 'left') {
          newTime = Math.min(newTime, end - 1000)
          this.$store.commit('SET_SELECTION', { start: newTime, end })
        } else {
          newTime = Math.max(newTime, start + 1000)
          this.$store.commit('SET_SELECTION', { start, end: newTime })
        }
        return
      }
      
      // Handle timeline panning
      if (this.isPanning) {
        const deltaX = e.clientX - this.panStartX
        
        // Only consider it a drag if moved more than 5px
        if (Math.abs(deltaX) > 5) {
          this.hasDragged = true
        }
        
        if (this.hasDragged) {
          const rect = this.$refs.canvas.getBoundingClientRect()
          const deltaTime = -(deltaX / rect.width) * this.viewDuration
          let newStart = this.panStartViewStart + deltaTime
          let newEnd = this.panStartViewEnd + deltaTime
          
          // Don't pan too far into the future
          const now = Date.now()
          const maxEnd = now + this.viewDuration * 0.5
          if (newEnd > maxEnd) {
            newEnd = maxEnd
            newStart = newEnd - this.viewDuration
          }
          
          // Lock view and update position
          this.$store.commit('SET_VIEW_LOCKED', true)
          this.$store.commit('SET_VIEW', { start: newStart, end: newEnd })
          
          // Exit live mode if panning to past
          if (this.isLive && newEnd < now - 1000) {
            this.$store.commit('SET_LIVE', false)
          }
        }
      }
    },
    handleMouseUp(e) {
      if (this.isSelecting) {
        // Check if this was a click (no drag) - snap to metadata block
        if (this.selectionIsClick) {
          const rect = this.$refs.canvas.getBoundingClientRect()
          const x = e.clientX - rect.left
          const clickTime = this.viewStart + (x / rect.width) * (this.viewEnd - this.viewStart)
          const block = this.findMetadataBlockAt(clickTime)
          
          if (block) {
            if (this.selectionShift && this.savedSelectionStart !== null) {
              // Shift+click: extend saved selection to include this block
              const newStart = Math.min(this.savedSelectionStart, block.start)
              const newEnd = Math.max(this.savedSelectionEnd, block.end)
              this.$store.commit('SET_SELECTION', { start: newStart, end: newEnd })
            } else {
              // Single click: select this block
              this.$store.commit('SET_SELECTION', { start: block.start, end: block.end })
            }
          }
        }
        this.$store.dispatch('endSelection')
      }
      if (this.draggingHandle) {
        this.$store.commit('SET_DRAGGING_HANDLE', null)
      }
      
      // If we were panning
      if (this.isPanning) {
        // If we didn't drag, treat as a click (seek)
        if (!this.hasDragged && !this.selectionMode) {
          const rect = this.$refs.canvas.getBoundingClientRect()
          const x = e.clientX - rect.left
          const t = this.viewStart + (x / rect.width) * (this.viewEnd - this.viewStart)
          
          // Check if clicked on an empty area (no segment)
          const hasSegment = this.segments.some(s => {
            const dur = s.actualDuration || s.duration || 60000
            return t >= s.timestamp && t < s.timestamp + dur
          })
          
          if (hasSegment) {
            this.$emit('seek', t)
          } else {
            // Clicked on empty area - pause playback
            this.$emit('pause-on-empty', t)
          }
        } else if (this.hasDragged) {
          // Finished dragging - fetch timeline data and metadata for new range
          this.$store.dispatch('fetchTimelineData')
          
          // Fetch metadata around the center of the new view
          const centerTime = this.viewStart + (this.viewDuration * 0.5)
          this.$store.dispatch('fetchMetadataPageAction', { aroundTimestamp: centerTime })
          
          // If was playing (or live), seek to center position and continue playing
          if (this.isPlaying || this.isLive) {
            this.$emit('seek', centerTime)
          }
        }
        
        this.isPanning = false
        this.hasDragged = false
      }
    },
    findMetadataBlockAt(timestamp) {
      const sorted = [...this.metadata].sort((a, b) => a.timestamp - b.timestamp)
      for (let i = 0; i < sorted.length; i++) {
        const m = sorted[i]
        const next = sorted[i + 1]
        const endTime = next ? next.timestamp : Date.now()
        if (timestamp >= m.timestamp && timestamp < endTime) {
          return { start: m.timestamp, end: endTime, metadata: m }
        }
      }
      return null
    },
    startDrag(handle, e) {
      e.preventDefault()
      this.$store.commit('SET_DRAGGING_HANDLE', handle)
      this.dragStartX = e.clientX
      const start = Math.min(this.selectionStart, this.selectionEnd)
      const end = Math.max(this.selectionStart, this.selectionEnd)
      this.dragStartTime = handle === 'left' ? start : end
    },
    handleWheel(e) {
      // Throttle to prevent too fast zooming
      if (this.wheelThrottled) return
      
      // Use wheel for stepping through time ranges (vertical scroll)
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        const timeRanges = this.$store.state.timeRanges
        const currentDuration = this.viewDuration
        
        // Find current range index
        let currentIndex = timeRanges.findIndex(r => r.ms === currentDuration)
        if (currentIndex === -1) {
          // Not on a preset, find closest
          currentIndex = timeRanges.findIndex(r => r.ms >= currentDuration) || 0
        }
        
        // Scroll down = zoom out (next range), scroll up = zoom in (previous range)
        let newIndex
        if (e.deltaY > 0) {
          // Zoom out - go to next (larger) range
          newIndex = Math.min(currentIndex + 1, timeRanges.length - 1)
        } else {
          // Zoom in - go to previous (smaller) range
          newIndex = Math.max(currentIndex - 1, 0)
        }
        
        if (newIndex !== currentIndex) {
          this.$store.dispatch('setViewDuration', timeRanges[newIndex].ms)
          
          // Throttle for 300ms
          this.wheelThrottled = true
          setTimeout(() => {
            this.wheelThrottled = false
          }, 300)
        }
      }
    }
  }
}
</script>

<style scoped>
.timeline-section { background: var(--white); border-bottom: 1px solid var(--gray-200); flex-shrink: 0; }
.timeline-wrapper { height: 175px; overflow: hidden; }
.timeline-detail { position: relative; height: 100%; background: var(--gray-50); overflow-x: auto; overflow-y: hidden; cursor: grab; }
.timeline-detail:active { cursor: grabbing; }
#timeline-canvas { height: 100%; display: block; }
#playhead { position: absolute; top: 0; width: 2px; height: 100%; background: var(--gray-800); pointer-events: none; z-index: 10; }
#playhead::before { content: ''; position: absolute; top: 50%; left: -5px; width: 12px; height: 12px; background: var(--gray-800); border-radius: 50%; transform: translateY(-50%); }
.time-axis { height: 22px; background: var(--gray-50); border-top: 1px solid var(--gray-200); position: relative; overflow: hidden; }
.time-axis :deep(.tick) { position: absolute; bottom: 4px; font-size: 10px; color: var(--gray-500); transform: translateX(-50%); white-space: nowrap; }
.selection-overlay { position: absolute; top: 0; height: 100%; background: rgba(38, 38, 38, 0.15); border-left: 2px solid var(--gray-800); border-right: 2px solid var(--gray-800); pointer-events: none; z-index: 5; }
.selection-overlay.hidden { display: none; }
.selection-handle { position: absolute; top: 0; width: 12px; height: 100%; cursor: ew-resize; pointer-events: auto; z-index: 10; }
.selection-handle::before { content: ''; position: absolute; top: 50%; transform: translateY(-50%); width: 4px; height: 40px; background: var(--gray-800); border-radius: 2px; }
.selection-handle::after { content: ''; position: absolute; top: 50%; transform: translateY(-50%); width: 0; height: 0; }
.selection-handle-left { left: -7px; }
.selection-handle-left::before { left: 4px; }
.selection-handle-left::after { left: 0; border-top: 6px solid transparent; border-bottom: 6px solid transparent; border-right: 6px solid var(--gray-800); }
.selection-handle-right { right: -7px; }
.selection-handle-right::before { right: 4px; }
.selection-handle-right::after { right: 0; border-top: 6px solid transparent; border-bottom: 6px solid transparent; border-left: 6px solid var(--gray-800); }
.selection-handle:hover::before { background: var(--gray-700); }
.selection-handle:hover::after { border-right-color: var(--gray-700); border-left-color: var(--gray-700); }
</style>
